/**
 * L'**équipe d'une session**, et ce que la couche hors-ligne en fait : le nom de la base qu'elle
 * ouvre (`offline/data-db.ts`). Une tablette passe de main en main, et l'agent qui ouvre une
 * session ne doit pas retrouver la tournée, les brouillons, les photos et la file d'envoi de
 * l'équipe précédente.
 *
 * ⚠️ **Ce module ne filtre rien, et il n'y a plus rien à filtrer** (D22 pt.4, #158) : une équipe
 * ne lit pas les données d'une autre parce que sa base **n'est pas ouverte**. Ce qui se décide
 * ici est donc une seule chose — *quelle* base — et c'est `offline/team-data.ts` qui l'ouvre.
 *
 * ⚠️ **Déclaration locale, pas une preuve** : la vue servie au terrain ne porte pas d'équipe, la
 * tablette range depuis sa propre session. C'est le serveur qui filtre par l'équipe du jeton ;
 * ce module protège l'agent suivant, pas le serveur.
 *
 * ⚠️ Ce module est **le plus bas de l'app** : il ne dépend de rien, et surtout pas de
 * `features/auth`. La conversion « session → équipe » vit à côté du type de session — sinon
 * `offline/` se met à dépendre de `features/`, l'inverse de la dépendance d'aujourd'hui.
 */

/**
 * Trois cas, pas deux :
 *
 * - `team` — une session avec équipe, le cas ordinaire ;
 * - `no_team` — une session **valide** sans équipe (M6) : l'agent existe, l'écran le dit
 *   (« Sans équipe »), et ses saisies ont droit à leur propre base. Ce n'est pas une erreur, et
 *   ce n'est pas non plus un fourre-tout partagé ;
 * - `unknown` — l'équipe n'est **pas encore lue**. La session locale vit dans une autre base,
 *   ouverte de façon asynchrone : entre le démarrage de l'app et sa lecture, on ne sait pas.
 *   Aucune base de tournée n'est alors ouverte — servir « tout » le temps d'une fenêtre de
 *   quelques millisecondes suffirait à montrer la tournée de l'équipe d'hier.
 */
export type TeamScope = { kind: 'team'; id: string } | { kind: 'no_team' } | { kind: 'unknown' }

/**
 * La base d'un agent sans équipe. Ni un identifiant d'équipe, ni l'absence de valeur : une base
 * à part entière, qu'aucune équipe nommée n'ouvre — ni un autre agent sans équipe, qui a la
 * même clé mais pas la même tablette.
 */
export const NO_TEAM_KEY = 'no-team'

/**
 * La clé qui **nomme la base** d'une session, `null` tant que l'équipe n'est pas connue.
 *
 * ⚠️ `null` n'est pas une base par défaut : c'est l'absence de base, donc l'absence de lecture
 * et d'écriture. Rendre une clé de repli ferait écrire les saisies d'un agent dans une base que
 * personne n'ouvrira jamais.
 *
 * ⚠️ La distinction ne tient que parce que {@link NO_TEAM_KEY} ne peut **pas** être un
 * identifiant d'équipe — ce n'est pas une uuid.
 */
export function knownScopeKeyOf(scope: TeamScope): string | null {
  if (scope.kind === 'unknown') return null

  return scope.kind === 'team' ? scope.id : NO_TEAM_KEY
}

/**
 * D'où la couche hors-ligne tient l'équipe de la session, et comment elle apprend qu'elle a
 * changé.
 *
 * Une forme structurelle, pas le store de session : la couche hors-ligne est la plus basse de
 * l'app et ne connaît ni `features/auth` ni les écrans. Le point d'entrée branche les deux.
 */
export interface ScopeSource {
  get(): TeamScope
  /** Prévient d'un changement de session ; rend de quoi se désabonner. */
  subscribe(listener: () => void): () => void
}

/**
 * Ce que la synchro doit savoir d'une session : **de quoi parler au serveur, et pour quelle
 * équipe**. Les deux voyagent ensemble, et c'est le point.
 *
 * ⚠️ **Un seul instantané, jamais deux lectures.** Prendre le jeton d'un côté et l'équipe de
 * l'autre laisse la porte à deux valeurs qui ne viennent pas de la même session : une descente
 * demanderait la tournée de l'équipe entrante pour la ranger dans la base de la sortante, où
 * personne ne la lirait plus.
 *
 * `accessToken: null` est un état de fonctionnement normal (session dégradée, D22 pt.1), pas une
 * panne — comme `scope.kind === 'unknown'` avant la lecture de la session locale.
 */
export interface SyncSession {
  accessToken: string | null
  scope: TeamScope
}
